import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    platformApi,
    type AuditLogFilters,
    type CreditRules,
    type ImpersonationSession,
    type NewTenantInput,
    type PlatformMetricFilters,
    type PlatformTenant,
    type PlatformTenantFilters,
} from '@/entities/tenant';
import { platformUserApi, type PlatformUserFilters } from '@/entities/user';
import { ulid } from '@/shared/api';

import { useImpersonationStore } from './impersonation/store';

export const adminKeys = {
    all: ['platform'] as const,
    tenants: (filters: PlatformTenantFilters) => ['platform', 'tenants', filters] as const,
    tenant: (uuid: string) => ['platform', 'tenant', uuid] as const,
    plans: (tenantUuid: string) => ['platform', 'plans', tenantUuid] as const,
    impersonations: (tenantUuid: string) => ['platform', 'impersonations', tenantUuid] as const,
    users: (filters: PlatformUserFilters) => ['platform', 'users', filters] as const,
    user: (uuid: string) => ['platform', 'user', uuid] as const,
    metrics: (filters: PlatformMetricFilters) => ['platform', 'metrics', filters] as const,
    audit: (filters: AuditLogFilters) => ['platform', 'audit-logs', filters] as const,
    templates: ['platform', 'templates'] as const,
};

export function usePlatformTenants(filters: PlatformTenantFilters) {
    return useQuery({
        queryKey: adminKeys.tenants(filters),
        queryFn: () => platformApi.tenants(filters),
        placeholderData: keepPreviousData,
    });
}

export function usePlatformTenant(tenantUuid: string) {
    return useQuery({
        queryKey: adminKeys.tenant(tenantUuid),
        queryFn: () => platformApi.tenant(tenantUuid),
        enabled: tenantUuid !== '',
    });
}

export function usePlans(tenantUuid: string) {
    return useQuery({
        queryKey: adminKeys.plans(tenantUuid),
        queryFn: () => platformApi.plans(tenantUuid),
        enabled: tenantUuid !== '',
        staleTime: 10 * 60 * 1_000,
    });
}

export function useTenantImpersonations(tenantUuid: string) {
    return useQuery({
        queryKey: adminKeys.impersonations(tenantUuid),
        queryFn: () => platformApi.impersonations(tenantUuid),
        enabled: tenantUuid !== '',
    });
}

export function usePlatformUsers(filters: PlatformUserFilters, enabled = true) {
    return useQuery({
        queryKey: adminKeys.users(filters),
        queryFn: () => platformUserApi.list(filters),
        enabled,
        placeholderData: keepPreviousData,
    });
}

export function usePlatformUser(userUuid: string) {
    return useQuery({
        queryKey: adminKeys.user(userUuid),
        queryFn: () => platformUserApi.find(userUuid),
        enabled: userUuid !== '',
    });
}

export function usePlatformMetrics(filters: PlatformMetricFilters, enabled = true) {
    return useQuery({
        queryKey: adminKeys.metrics(filters),
        queryFn: () => platformApi.metrics(filters),
        enabled,
    });
}

export function useAuditLogs(filters: AuditLogFilters) {
    return useQuery({
        queryKey: adminKeys.audit(filters),
        queryFn: () => platformApi.auditLogs(filters),
        placeholderData: keepPreviousData,
    });
}

export function usePlatformTemplates() {
    return useQuery({
        queryKey: adminKeys.templates,
        queryFn: () => platformApi.templates(),
        staleTime: 10 * 60 * 1_000,
    });
}

/** Een nieuw bedrijf staat daarna in de lijst en in de audit log. */
export function useCreateTenant() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: NewTenantInput) => platformApi.createTenant(input),
        onSuccess: (provisioned) => {
            client.setQueryData(adminKeys.tenant(provisioned.tenant.uuid), provisioned.tenant);
            void client.invalidateQueries({ queryKey: ['platform', 'tenants'] });
            void client.invalidateQueries({ queryKey: ['platform', 'audit-logs'] });
        },
    });
}

export function useRollOutTemplate() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { templateUuid: string; tenantUuids: readonly string[] }) =>
            platformApi.rollOutTemplate(input),
        onSuccess: () => {
            void client.invalidateQueries({ queryKey: ['platform', 'audit-logs'] });
        },
    });
}

/** Wat een wijziging aan een organisatie nog meer raakt: de lijst en de audit log. */
function useTenantChanged() {
    const client = useQueryClient();

    return (tenant: PlatformTenant) => {
        client.setQueryData(adminKeys.tenant(tenant.uuid), tenant);
        void client.invalidateQueries({ queryKey: ['platform', 'tenants'] });
        void client.invalidateQueries({ queryKey: ['platform', 'audit-logs'] });
    };
}

export function useSuspendTenant(tenantUuid: string) {
    const changed = useTenantChanged();

    return useMutation({
        mutationFn: (reason: string) => platformApi.suspend(tenantUuid, reason),
        onSuccess: changed,
    });
}

export function useResumeTenant(tenantUuid: string) {
    const changed = useTenantChanged();

    return useMutation({
        mutationFn: () => platformApi.resume(tenantUuid),
        onSuccess: changed,
    });
}

export function useChangePlan(tenantUuid: string) {
    const changed = useTenantChanged();

    return useMutation({
        mutationFn: (planCode: string) => platformApi.changePlan(tenantUuid, planCode),
        onSuccess: changed,
    });
}

/**
 * Tegoedregels van een bedrijf: blokkeren bij nul en de ruimte onder nul. Alleen het
 * platform zet ze; de audit log krijgt de oude en de nieuwe regels.
 */
export function useChangeCreditRules(tenantUuid: string) {
    const changed = useTenantChanged();

    return useMutation({
        mutationFn: (rules: CreditRules) => platformApi.changeCreditRules(tenantUuid, rules),
        onSuccess: changed,
    });
}

/**
 * Handmatige tegoedboeking. De reden is verplicht en gaat mee naar de audit log;
 * de UI laat een boeking zonder reden niet eens vertrekken. De sleutel hoort bij
 * de poging: een herhaling na een weggevallen verbinding boekt niet dubbel.
 */
export function useBookCredits(tenantUuid: string) {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { credits: number; reason: string; idempotencyKey?: string }) =>
            platformApi.bookCredits({
                tenantUuid,
                credits: input.credits,
                reason: input.reason,
                idempotencyKey: input.idempotencyKey ?? ulid(),
            }),
        onSuccess: () => {
            void client.invalidateQueries({ queryKey: ['platform', 'audit-logs'] });
        },
    });
}

/**
 * Meekijken start pas als er een organisatie, een gebruiker én een reden is. De
 * sessie komt daarna in de store, en dus in de balk bovenaan het scherm.
 */
export function useStartImpersonation(tenant: { uuid: string; name: string }) {
    const start = useImpersonationStore((state) => state.start);
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: {
            userUuid: string;
            userName: string;
            reason: string;
            minutes?: number;
        }): Promise<ImpersonationSession> =>
            platformApi.impersonate({
                tenantUuid: tenant.uuid,
                tenantName: tenant.name,
                ...input,
            }),
        onSuccess: (session) => {
            start(session);
            void client.invalidateQueries({ queryKey: adminKeys.impersonations(tenant.uuid) });
            void client.invalidateQueries({ queryKey: ['platform', 'audit-logs'] });
        },
    });
}

export function useEndImpersonation(tenantUuid: string) {
    const client = useQueryClient();
    const current = useImpersonationStore((state) => state.session);
    const stop = useImpersonationStore((state) => state.stop);

    return useMutation({
        mutationFn: (sessionUuid: string) => platformApi.endImpersonation(sessionUuid),
        onSettled: (_result, _error, sessionUuid) => {
            if (current?.sessionUuid === sessionUuid) {
                stop();
            }

            void client.invalidateQueries({ queryKey: adminKeys.impersonations(tenantUuid) });
            void client.invalidateQueries({ queryKey: ['platform', 'audit-logs'] });
        },
    });
}
